import React, { useState, useId } from "react";
import { Select } from "@sprout/select";
import { type LocationControlResult } from "@grnhse/location-control";
import { useLocationControl } from "@hooks/use_location_control";
import LocationIcon from "@sprout/icons/location";
import useTranslation from "@hooks/use_translation";

export type Location = {
  display_name: string;
  latitude: number;
  longitude: number;
};

type SharedProps = {
  value: Location | null;
  onChange: (newValue: Location | null) => void;
  helperText?: string;
  label?: string;
  icon?: React.ReactNode;
};

type Props = Omit<SharedProps, "label" | "icon">;

const SharedLocationInput = ({ value, onChange, helperText, label, icon }: SharedProps) => {
  const id = useId();
  const locationControl = useLocationControl();
  const { t } = useTranslation("user");

  const selectedOption = value ? { value: 0, label: value.display_name } : undefined;
  const placeholderText = label ? undefined : t("fields.location");

  const [isLoading, setIsLoading] = useState(false);

  const loadOptions = async (inputValue: string) => {
    try {
      setIsLoading(true);

      return locationControl.results(inputValue);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = async (selectedLocation: LocationControlResult) => {
    const newValue = await locationControl.resultSelected(selectedLocation);

    if (!newValue) {
      return onChange(null);
    }

    onChange({
      display_name: newValue.displayName,
      latitude: newValue.latitude,
      longitude: newValue.longitude,
    });
  };

  return (
    <Select
      id={id}
      startIcon={icon}
      label={label}
      onSelect={(selectedOption) => handleChange(selectedOption as LocationControlResult)}
      loadOptions={loadOptions}
      value={selectedOption}
      isLoading={isLoading}
      isAsync
      isClearable
      hideNoOptionsMessage
      hideDropdownIndicator
      helperText={helperText}
      placeholderText={placeholderText}
    />
  );
};

export const LocationInput = (props: Props) => {
  const { t } = useTranslation("user");

  return <SharedLocationInput {...props} label={t("fields.location")} />;
};

export const IconLocationInput = (props: Props) => {
  return <SharedLocationInput {...props} icon={<LocationIcon />} />;
};
